import { z } from 'zod';

/**
 * Environment variable schema for runtime validation
 * This ensures all required environment variables are present and valid
 */
const envSchema = z.object({
  VITE_APP_GRAPHQL_URL: z
    .string()
    .url('VITE_APP_GRAPHQL_URL must be a valid URL')
    .describe('AppSync GraphQL API endpoint URL'),

  VITE_APP_GRAPHQL_API_KEY: z
    .string()
    .min(1, 'VITE_APP_GRAPHQL_API_KEY cannot be empty if provided')
    .optional()
    .describe('AppSync GraphQL API key for public access'),

  VITE_APP_COGNITO_USER_POOL_ID: z.string().describe('AWS Cognito User Pool ID'),

  VITE_APP_COGNITO_CLIENT_ID: z.string().describe('AWS Cognito Client ID'),

  VITE_APP_COGNITO_REGION: z
    .string()
    .min(1, 'VITE_APP_COGNITO_REGION is required')
    .describe('AWS region for Cognito (e.g., ap-southeast-2)'),

  VITE_APP_COGNITO_DOMAIN: z
    .string()
    .min(1, 'VITE_APP_COGNITO_DOMAIN cannot be empty if provided')
    .describe('AWS Cognito domain (optional, will be auto-constructed if not provided)'),

  VITE_APP_BUILD_ENV: z
    .enum(['local', 'dev', 'prod'])
    .default('dev')
    .describe('Build environment (local, dev or prod)'),
});

/**
 * Get validated environment variables
 * This will throw an error at runtime if required variables are missing or invalid
 */
function getEnv() {
  try {
    return envSchema.parse({
      VITE_APP_GRAPHQL_URL: import.meta.env.VITE_APP_GRAPHQL_URL,
      VITE_APP_GRAPHQL_API_KEY: import.meta.env.VITE_APP_GRAPHQL_API_KEY,
      VITE_APP_COGNITO_USER_POOL_ID: import.meta.env.VITE_APP_COGNITO_USER_POOL_ID,
      VITE_APP_COGNITO_CLIENT_ID: import.meta.env.VITE_APP_COGNITO_CLIENT_ID,
      VITE_APP_COGNITO_REGION: import.meta.env.VITE_APP_COGNITO_REGION,
      VITE_APP_COGNITO_DOMAIN: import.meta.env.VITE_APP_COGNITO_DOMAIN,
      VITE_APP_BUILD_ENV: import.meta.env.VITE_APP_BUILD_ENV,
    });
  } catch (error) {
    if (error instanceof z.ZodError) {
      const missingVars = error.errors
        .map((err) => `${err.path.join('.')}: ${err.message}`)
        .join('\n');

      throw new Error(
        `❌ Invalid environment variables:\n\n${missingVars}\n\n` +
          'Please check your .env.local file or environment configuration.\n' +
          'See .env.example for required variables.',
      );
    }
    throw error;
  }
}

/**
 * Validated environment variables
 * Access this object instead of import.meta.env directly for type safety
 * Uses getters to re-evaluate on each access, allowing tests to stub env vars
 */
export const env = new Proxy({} as z.infer<typeof envSchema>, {
  get(_target, prop: string) {
    const validated = getEnv();
    return validated[prop as keyof typeof validated];
  },
});

/**
 * Type-safe environment variable access
 * Use this type when you need to reference environment variable types
 */
export type Env = z.infer<typeof envSchema>;
